import type { ApiClient } from '../apiClient';

export interface KitStatus {
  hasKit: boolean;
  hint: string | null;
  createdAt: string | null;
  /** The kit predates a device change and no longer matches the bound key. */
  stale?: boolean;
}

export interface EscrowBlob {
  wrappedKey: string;
  salt: string;
  iv: string;
  iterations: number;
  hint: string;
  createdAt: string;
  rotatedAt: string | null;
}

export interface RecoveryView {
  id: string;
  status: 'requested' | 'ready' | 'completed' | 'cancelled' | 'expired';
  requestedAt: string;
  coolingOffEndsAt: string;
  attestedAt: string | null;
  requestIp: string | null;
  requestUserAgent: string | null;
}

export function getKitStatus(api: ApiClient): Promise<KitStatus> {
  return api.get<KitStatus>('/api/recovery/kit/status');
}

export function putKit(
  api: ApiClient,
  wrapped: { wrappedKey: string; salt: string; iv: string; iterations: number; hint: string },
): Promise<{ stored: true; rotated: boolean }> {
  return api.post<{ stored: true; rotated: boolean }>('/api/recovery/kit', wrapped);
}

export function getKitBlob(api: ApiClient): Promise<EscrowBlob> {
  return api.get<EscrowBlob>('/api/recovery/kit');
}

export function listMyRecoveries(api: ApiClient): Promise<{ recoveries: RecoveryView[] }> {
  return api.get<{ recoveries: RecoveryView[] }>('/api/recovery/mine');
}

/**
 * The three routes below are used by people who cannot sign in, so they are
 * plain fetches rather than ApiClient calls — ApiClient attaches a bearer and
 * tries to refresh on 401, and there is no session here to refresh.
 */
export async function requestRecovery(email: string): Promise<{ coolingOffHours: number }> {
  const res = await fetch('/api/recovery/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Could not start the recovery.');
  return res.json() as Promise<{ coolingOffHours: number }>;
}

export async function stopRecovery(token: string, reason?: string): Promise<{ stopped: boolean }> {
  const res = await fetch('/api/recovery/stop', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, reason }),
  });
  if (!res.ok) throw new Error('Could not stop the recovery.');
  return res.json() as Promise<{ stopped: boolean }>;
}

export async function sendFinishCode(email: string): Promise<void> {
  await fetch('/api/recovery/finish/code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}
